import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogIn, UserPlus, Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    if (isRegister && !formData.name) {
      toast.error('Preencha seu nome');
      return;
    }

    setLoading(true);
    
    try {
      const endpoint = isRegister ? '/api/auth/register' : '/api/auth/login';
      const body = isRegister 
        ? { email: formData.email, password: formData.password, name: formData.name }
        : { email: formData.email, password: formData.password };
      
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erro ao autenticar');
      }

      toast.success(data.message || (isRegister ? 'Cadastro realizado!' : 'Login realizado!'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Erro ao autenticar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1676746424139-77f8bd8922a8)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      data-testid="login-page"
    >
      <div className="noise-overlay" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#090A0F] via-black/80 to-transparent" />
      
      <div className="relative z-10 glass-card p-8 sm:p-12 max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-3 text-white" style={{ fontFamily: 'Outfit' }}>
            Copa <span className="text-[#FF5A00]">Coruja</span>
          </h1>
          <p className="text-[#A1A1AA] text-lg" style={{ fontFamily: 'Manrope' }}>
            {isRegister ? 'Crie sua conta' : 'Acesse sua conta'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isRegister && (
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#A1A1AA]" />
              <Input
                type="text"
                placeholder="Seu nome"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#A1A1AA] h-12"
                data-testid="name-input"
              />
            </div>
          )}
          
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#A1A1AA]" />
            <Input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#A1A1AA] h-12"
              data-testid="email-input"
            />
          </div>
          
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#A1A1AA]" />
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Senha"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-[#A1A1AA] h-12"
              data-testid="password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#A1A1AA] hover:text-white"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>

          <Button
            type="submit"
            className="w-full btn-primary text-lg py-6 h-14"
            disabled={loading}
            data-testid="submit-button"
          >
            {loading ? (
              'Carregando...'
            ) : isRegister ? (
              <>
                <UserPlus className="mr-2 h-5 w-5" />
                Criar Conta
              </>
            ) : (
              <>
                <LogIn className="mr-2 h-5 w-5" />
                Entrar
              </>
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setIsRegister(!isRegister);
              setFormData({ email: '', password: '', name: '' });
            }}
            className="text-[#A1A1AA] hover:text-white transition-colors"
          >
            {isRegister ? (
              <>Já tem conta? <span className="text-[#FF5A00] font-semibold">Entrar</span></>
            ) : (
              <>Não tem conta? <span className="text-[#FF5A00] font-semibold">Cadastre-se</span></>
            )}
          </button>
        </div>

        <p className="text-center text-sm text-[#A1A1AA] mt-6">
          Gerencie seu time da Copa Coruja com estilo
        </p>
      </div>
    </div>
  );
}
